import ReactGA from 'react-ga4';

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

class GoogleAnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.debugMode = process.env.NODE_ENV === 'development';
  }

  // Initialize Google Analytics
  initialize() {
    if (!MEASUREMENT_ID) {
      console.warn('🔍 GA4: No measurement ID found in environment variables');
      return false;
    }

    if (this.isInitialized) {
      console.log('🔍 GA4: Already initialized');
      return true;
    }

    try {
      ReactGA.initialize(MEASUREMENT_ID, {
        debug: this.debugMode,
        testMode: this.debugMode,
        gtagOptions: {
          debug_mode: this.debugMode
        }
      });

      this.isInitialized = true;
      console.log('✅ GA4: Initialized successfully with ID:', MEASUREMENT_ID);
      
      // Send initial page view
      this.trackPageView(window.location.pathname + window.location.search);
      
      return true;
    } catch (error) {
      console.error('❌ GA4: Initialization failed:', error);
      return false;
    }
  }

  // Track page views
  trackPageView(path) {
    if (!this.isInitialized) return;

    try {
      ReactGA.send({ 
        hitType: 'pageview', 
        page: path,
        title: document.title 
      });
      
      if (this.debugMode) {
        console.log('📄 GA4: Page view tracked:', path);
      }
    } catch (error) {
      console.error('❌ GA4: Page view tracking failed:', error);
    }
  }

  // Track custom events
  trackEvent(action, parameters = {}) {
    if (!this.isInitialized) return;

    try {
      ReactGA.event(action, parameters);
      
      if (this.debugMode) {
        console.log('🎯 GA4: Event tracked:', action, parameters);
      }
    } catch (error) {
      console.error('❌ GA4: Event tracking failed:', error);
    }
  }

  // VoxSmyth-specific tracking methods
  trackAudiobookPlay(bookTitle, bookId, chapterNumber = null) {
    this.trackEvent('audiobook_play', {
      book_title: bookTitle,
      book_id: bookId,
      chapter: chapterNumber,
      category: 'audiobook'
    });
  }

  trackVoiceSelection(characterName, voiceName, bookTitle) {
    this.trackEvent('voice_selection', {
      character_name: characterName,
      voice_name: voiceName,
      book_title: bookTitle,
      category: 'voice_management'
    });
  }

  trackCharacterAnalysis(bookTitle, charactersFound, analysisType = 'smart') {
    this.trackEvent('character_analysis', {
      book_title: bookTitle,
      characters_count: charactersFound,
      analysis_type: analysisType,
      category: 'ai_analysis'
    });
  }

  trackUserRegistration(registrationMethod = 'email') {
    this.trackEvent('sign_up', {
      method: registrationMethod,
      category: 'user_authentication'
    });
  }

  trackUserLogin(loginMethod = 'email') {
    this.trackEvent('login', {
      method: loginMethod,
      category: 'user_authentication'
    });
  }

  trackSubscriptionPurchase(tier, price, currency = 'USD') {
    this.trackEvent('purchase', {
      transaction_id: Date.now().toString(),
      value: price,
      currency: currency,
      items: [{
        item_id: `subscription_${tier}`,
        item_name: `VoxSmyth ${tier} Tier`,
        category: 'subscription',
        quantity: 1,
        price: price
      }]
    });
  }

  trackBookUpload(bookTitle, bookSize, bookFormat) {
    this.trackEvent('book_upload', {
      book_title: bookTitle,
      file_size: bookSize,
      file_format: bookFormat,
      category: 'content_creation'
    });
  }

  trackAudioGeneration(bookTitle, chapterCount, voiceCount, generationType = 'multi_voice') {
    this.trackEvent('audio_generation', {
      book_title: bookTitle,
      chapters: chapterCount,
      voices_used: voiceCount,
      generation_type: generationType,
      category: 'ai_generation'
    });
  }

  trackSearch(searchTerm, resultsCount = null) {
    this.trackEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount,
      category: 'user_interaction'
    });
  }

  trackFeedbackSubmission(feedbackType, bookTitle = null) {
    this.trackEvent('feedback_submit', {
      feedback_type: feedbackType,
      book_title: bookTitle,
      category: 'user_feedback'
    });
  }

  // Track user properties
  setUserProperties(userId, tier = null, registrationDate = null) {
    if (!this.isInitialized) return;

    try {
      ReactGA.set({ 
        user_id: userId,
        custom_map: { 
          subscription_tier: tier,
          registration_date: registrationDate
        }
      });

      if (this.debugMode) {
        console.log('👤 GA4: User properties set:', { userId, tier, registrationDate });
      }
    } catch (error) {
      console.error('❌ GA4: User properties failed:', error);
    }
  }

  // Enhanced ecommerce tracking
  trackPurchaseFlow(step, bookTitle = null, tier = null) {
    const purchaseSteps = {
      'view_subscription_plans': 'Viewed subscription plans',
      'begin_checkout': 'Started checkout process', 
      'add_payment_info': 'Added payment information',
      'purchase_complete': 'Completed purchase'
    };

    this.trackEvent(step, {
      step_name: purchaseSteps[step] || step,
      book_title: bookTitle,
      subscription_tier: tier,
      category: 'ecommerce'
    });
  }

  // Admin Portal Specific Tracking
  trackAdminLogin(adminEmail) {
    this.trackEvent('admin_login', {
      admin_email: adminEmail,
      category: 'admin_portal'
    });
  }

  trackAdminAction(actionType, targetType = null, targetId = null, details = {}) {
    this.trackEvent('admin_action', {
      action_type: actionType,
      target_type: targetType,
      target_id: targetId,
      category: 'admin_portal',
      ...details
    });
  }

  trackUserManagement(action, userId = null, userTier = null) {
    this.trackEvent('admin_user_management', {
      action: action, // 'create', 'edit', 'delete', 'tier_change'
      user_id: userId,
      user_tier: userTier,
      category: 'admin_portal'
    });
  }

  trackContentManagement(action, contentType, contentId = null, details = {}) {
    this.trackEvent('admin_content_management', {
      action: action, // 'analyze', 'split_chapters', 'restore_content'
      content_type: contentType, // 'book', 'chapter', 'analysis'
      content_id: contentId,
      category: 'admin_portal',
      ...details
    });
  }

  trackPromoCodeManagement(action, promoCodeId = null, discountPercentage = null) {
    this.trackEvent('admin_promo_management', {
      action: action, // 'create', 'edit', 'delete', 'activate', 'deactivate'
      promo_code_id: promoCodeId,
      discount_percentage: discountPercentage,
      category: 'admin_portal'
    });
  }

  trackAnalyticsView(viewType, dateRange = null) {
    this.trackEvent('admin_analytics_view', {
      view_type: viewType, // 'dashboard', 'users', 'revenue', 'engagement'
      date_range: dateRange,
      category: 'admin_portal'
    });
  }

  trackFeedbackManagement(action, feedbackId = null, feedbackType = null) {
    this.trackEvent('admin_feedback_management', {
      action: action, // 'view', 'respond', 'archive', 'delete'
      feedback_id: feedbackId,
      feedback_type: feedbackType,
      category: 'admin_portal'
    });
  }

  // Get analytics data (for admin dashboard)
  async getAnalyticsData(dateRange = '30d') {
    // Track the analytics data request
    this.trackEvent('admin_analytics_data_request', {
      date_range: dateRange,
      category: 'admin_portal'
    });

    // Return structured data for admin dashboard
    // Note: In a real implementation, this would fetch from GA4 Reporting API
    return {
      users: {
        total: 0,
        active: 0,
        new: 0
      },
      sessions: {
        total: 0,
        average_duration: 0,
        bounce_rate: 0
      },
      events: [],
      revenue: {
        total: 0,
        subscriptions: 0
      },
      content: {
        audiobooks_played: 0,
        characters_analyzed: 0,
        audio_generated: 0
      }
    };
  }
}

// Create singleton instance
const googleAnalytics = new GoogleAnalyticsService();

export default googleAnalytics;